/**
 * A note's reactions, broken out by emoji — and where you send one.
 *
 * The action row's heart is one total and cannot show *what* people reacted with —
 * and for a NIP-30 custom emoji reaction it discards the thing the reactor actually
 * chose: a kind-7 whose content is `:soapbox:` counts as one anonymous heart and
 * the image never appears anywhere in the UI. This row is where it appears, and now
 * also where the reader answers it.
 *
 * Two rules about the wording, in the spirit of every other count in this app:
 *
 *  - The trailing line says the chips are what *this device holds*. Reaction
 *    queries are bounded and our relay set is not the network, so a chip reading
 *    "🔥 12" is a floor. Without the line it reads as the note's twelve fire
 *    reactions, full stop.
 *  - Nothing here is a percentage or a ranking of accounts. Counts are distinct
 *    pubkeys within the sample, ordered by size with a deterministic tie-break so
 *    the chips do not shuffle as reactions arrive.
 *
 * ## Why pressing a chip is the whole of custom-emoji sending
 *
 * A chip carries both halves a NIP-30 reaction needs — the shortcode and the image
 * URL its author published — so re-reacting with one requires no emoji-pack browser
 * and no kind-30030 fetch. The picker beside the chips offers Unicode emoji only,
 * which is the honest boundary: Setu can send any custom emoji it has *seen*, and
 * does not pretend to a library it has not loaded.
 */

import { cn } from "@setu/ui";
import { EmojiPicker } from "../compose/EmojiPicker";
import { CustomEmoji } from "./CustomEmoji";
import type { GroupedReactions, ReactionGroup } from "./noteReactions";
import { choiceForGroup, type ReactionChoice } from "./reactionChoice";

export interface ReactionRowProps {
  reactions: GroupedReactions;
  className?: string;
  /**
   * Publish or withdraw a reaction. Absent renders the chips as static text.
   *
   * Absence is the honest rendering for a surface with no publish path wired up,
   * and it is what the feed uses: a chip that looked pressable and did nothing
   * would be worse than one that plainly is not — the same rule the action row
   * follows for a read-only session.
   */
  onReact?(choice: ReactionChoice, active: boolean): void;
  /** True while a reaction on this note is in flight, so chips stop accepting. */
  busy?: boolean;
}

export function ReactionRow({
  reactions,
  className,
  onReact,
  busy = false,
}: ReactionRowProps) {
  // Nothing to show and no way to add: render nothing at all.
  if (reactions.groups.length === 0 && !onReact) return null;

  return (
    <div className={cn("mt-1.5 flex flex-wrap items-center gap-1", className)}>
      {reactions.groups.map((group) => (
        <ReactionChip
          key={group.key}
          group={group}
          {...(onReact ? { onReact } : {})}
          busy={busy}
        />
      ))}
      {onReact ? (
        <EmojiPicker
          label="Add a reaction"
          disabled={busy}
          onPick={(emoji) => onReact({ kind: "emoji", content: emoji }, false)}
        />
      ) : null}
      {/* The caption is not optional. A row of counts with no denominator reads as
          the note's reaction totals, which is a claim no client can make. Omitted
          at zero, where there is no sample to describe and the row is just the
          picker. */}
      {reactions.groups.length > 0 ? (
        <span className="ml-1 text-2xs text-muted-foreground">
          from {reactions.events}{" "}
          {reactions.events === 1 ? "reaction" : "reactions"} this device holds
        </span>
      ) : null}
    </div>
  );
}

/** The chip's shared look, so the button and the static span cannot drift apart. */
function chipClass(viewerReacted: boolean): string {
  return cn(
    "inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5",
    "setu-mono text-2xs tabular-nums",
    viewerReacted
      ? "border-primary/60 bg-primary/10"
      : "border-border/60 bg-muted/40",
  );
}

/** The emoji itself: the published image when there is one, else the characters. */
function ChipFace({ group }: { group: ReactionGroup }) {
  return (
    <>
      {group.imageUrl === undefined ? (
        <span aria-hidden className="text-sm leading-none">
          {group.label}
        </span>
      ) : (
        <CustomEmoji url={group.imageUrl} label={group.label} />
      )}
      <span className="text-muted-foreground">{group.count}</span>
    </>
  );
}

/**
 * One reaction and its count.
 *
 * A button when the surface can publish and the group can be turned back into a
 * choice, a span otherwise. The second case is real rather than defensive: a chip
 * whose grouping key is not a shortcode it could re-publish would produce a
 * malformed `emoji` tag, and a control that can only fail should not look like one
 * that works.
 */
function ReactionChip({
  group,
  onReact,
  busy,
}: {
  group: ReactionGroup;
  onReact?(choice: ReactionChoice, active: boolean): void;
  busy: boolean;
}) {
  const choice = onReact ? choiceForGroup(group) : undefined;

  if (!onReact || choice === undefined) {
    return (
      <span className={chipClass(group.viewerReacted)}>
        <ChipFace group={group} />
        <span className="sr-only">
          {group.label}
          {group.viewerReacted ? ", including you" : ""}
        </span>
      </span>
    );
  }

  return (
    <button
      type="button"
      // The count is in the visible face; the name says what pressing does and
      // whether the reader is already in it, which the number cannot convey.
      aria-label={`${group.viewerReacted ? "Remove your" : "Add a"} ${group.label} reaction`}
      aria-pressed={group.viewerReacted}
      aria-disabled={busy || undefined}
      onClick={busy ? undefined : () => onReact(choice, group.viewerReacted)}
      className={cn(
        chipClass(group.viewerReacted),
        "transition-colors duration-(--motion-duration-instant)",
        "focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-hidden",
        busy ? "cursor-not-allowed opacity-60" : "hover:border-primary/60",
      )}
    >
      <ChipFace group={group} />
    </button>
  );
}
